import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Search, Store } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { listComparisons } from '@/lib/comparison.functions';
import { money } from '@/lib/comparison';

type ComparisonSearchProps = {
  initialValue: string;
  onSearch: (query: string) => void;
  onOpenProduct: (path: string) => void;
};

export function ComparisonSearch({ initialValue, onSearch, onOpenProduct }: ComparisonSearchProps) {
  const [value, setValue] = useState(initialValue);
  const [debounced, setDebounced] = useState(initialValue);
  const [active, setActive] = useState(-1);
  const [focused, setFocused] = useState(false);

  useEffect(() => setValue(initialValue), [initialValue]);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [value]);

  const enabled = focused && debounced.length >= 2;
  const { data, isFetching } = useQuery({
    queryKey: ['comparison-suggestions', debounced],
    queryFn: () => listComparisons({ data: { q: debounced, limit: 6, sort: 'stores' } }),
    enabled,
    staleTime: 60_000,
  });
  const suggestions = useMemo(() => data?.items ?? [], [data?.items]);
  const submit = () => onSearch(value.trim());
  const open = (index: number) => {
    const product = suggestions[index];
    if (!product) return;
    setFocused(false);
    onOpenProduct(product.slug ? `/produto/${product.slug}` : `/comparador-de-precos/${product.id}`);
  };

  return (
    <div className="relative max-w-2xl">
      <form onSubmit={(event) => { event.preventDefault(); active >= 0 ? open(active) : submit(); }}>
        <Search className="pointer-events-none absolute left-4 top-1/2 z-10 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={value}
          onChange={(event) => { setValue(event.target.value); setActive(-1); }}
          onFocus={() => setFocused(true)}
          onBlur={() => window.setTimeout(() => setFocused(false), 150)}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') { event.preventDefault(); setActive((index) => Math.min(index + 1, suggestions.length - 1)); }
            if (event.key === 'ArrowUp') { event.preventDefault(); setActive((index) => Math.max(index - 1, -1)); }
          }}
          placeholder="Pesquisar dentro do comparador..."
          aria-label="Pesquisar dentro do comparador"
          className="h-12 bg-card pl-11 pr-24 shadow-sm"
        />
        <Button type="submit" size="sm" className="absolute right-1.5 top-1.5 h-9">Buscar</Button>
      </form>

      {enabled && (isFetching || suggestions.length > 0) && (
        <div className="absolute inset-x-0 top-[calc(100%+0.5rem)] z-40 overflow-hidden rounded-lg border bg-card shadow-xl">
          {isFetching && !suggestions.length && <p className="flex items-center gap-2 px-4 py-4 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Buscando produtos…</p>}
          {suggestions.map((product, index) => (
            <Button
              key={product.id}
              type="button"
              variant="ghost"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => open(index)}
              className={`h-auto w-full justify-start gap-3 rounded-none px-4 py-3 text-left ${active === index ? 'bg-muted' : ''}`}
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-background">
                {product.image_url ? <img src={product.image_url} alt="" className="h-full w-full object-contain p-1" /> : <Search className="h-4 w-4 text-muted-foreground" />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-semibold text-foreground">{product.title}</span>
                <span className="mt-0.5 flex items-center gap-1 text-xs font-normal text-muted-foreground">
                  <Store className="h-3 w-3" /> {product.store_count ?? product.offers.length} lojas · a partir de {money(Math.min(...product.offers.map((offer) => offer.price)))}
                </span>
              </span>
            </Button>
          ))}
          <Button type="button" variant="ghost" onMouseDown={(event) => event.preventDefault()} onClick={submit} className="h-auto w-full justify-start rounded-none border-t px-4 py-3 text-primary">
            <Search className="mr-2 h-4 w-4" /> Ver todos os resultados para “{value.trim()}”
          </Button>
        </div>
      )}
    </div>
  );
}